import { Component, inject, Input, ChangeDetectionStrategy } from '@angular/core';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { ApiService, UtilsService } from 'src/app/services';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { WorkingContract } from 'src/app/models/working_contract.model';
import { Role } from 'src/app/models/role.model';
import { DepartmentCategory } from 'src/app/models/department_category.model';
import { WorkingContractType } from 'src/app/models/working_contract_type.model';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UntypedFormBuilder, FormsModule, ReactiveFormsModule, FormGroup, Validators, FormControl } from '@angular/forms';
import dayjs from 'dayjs';
import { User } from 'src/app/models/user.model';
import { WorkingContractVariableCondition } from 'src/app/models/working_contract_variable_condition.model';
import { WorkingContractVariableConditionDialogComponent } from '../working-contract-variable-condition-dialog/working-contract-variable-condition-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  DatePickerDialogComponent,
  DatePickerDialogData,
} from 'src/app/components/main/shared/date-picker-dialog/date-picker-dialog.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-working-contract-variable-condition-list',
  templateUrl: './working-contract-variable-condition-list.component.html',
  styleUrl: './working-contract-variable-condition-list.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatButtonModule,
    MatDatepickerModule,
    MatFormField,
    MatInputModule,
    MatTooltip,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
  ],
})
export class WorkingContractVariableConditionListComponent extends IndexTemplateComponent {
  private api = inject(ApiService);
  private app_utils = inject(UtilsService);
  public permissions = inject(PermissionsService);
  private dialog = inject(MatDialog);
  private snack = inject(MatSnackBar);

  displayedColumns: string[] = ['is_active', 'start_date', 'end_date', 'actions'];

  dataSource: MatTableDataSource<WorkingContractVariableCondition> = new MatTableDataSource<WorkingContractVariableCondition>();

  @Input() working_contract: WorkingContract;
  @Input() working_contract_types: WorkingContractType[];
  @Input() user: User;
  @Input() its_me: boolean;
  @Input() roles: Role[];
  @Input() department_categories: DepartmentCategory[];
  canManageUserContractForm: boolean = false;
  canManageVariableConditionsAdminActions: boolean = false;

  formGroups: {
    [key: number]: FormGroup<{
      start_date: FormControl<any>;
      end_date: FormControl<any>;
    }>;
  } = {};

  now: Date = new Date();
  tomorrow: Date = dayjs().add(1, 'day').startOf('day').toDate();

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));

    this.filterForm.removeControl('search');
  }

  override fetchData(): void {
    this.canManageUserContractForm = this.user.role.slug === 'worker' && this.permissions.validateDepartment(['direction', 'rrhh']);
    this.canManageVariableConditionsAdminActions = this.permissions.validateDepartment(['direction', 'rrhh']);

    this.api.getWorkingContractWorkingContractVariableConditionsNowAndFuture(this.working_contract.id).subscribe((data: any) => {
      this.dataSource.data = data;

      this.dataSource.data.forEach((record: WorkingContractVariableCondition): void => {
        const endDateControl = new FormControl(record.end_date, Validators.compose([]));
        if (this.isEndDateDisabled(record)) {
          endDateControl.disable();
        }

        this.formGroups[record.id] = this.fb.group({
          start_date: [record.start_date, Validators.required],
          end_date: endDateControl,
        });
      });
    });
  }

  openWorkingContractVariableConditionDialogComponent(workingContractVariableCondition: WorkingContractVariableCondition): void {
    const workingContract = this.working_contract;

    workingContract.current_working_contract_variable_condition = workingContractVariableCondition;

    this.dialog
      .open(WorkingContractVariableConditionDialogComponent, {
        data: {
          working_contract: workingContract,
          working_contract_types: this.working_contract_types,
          user: this.user,
          its_me: this.its_me,
          roles: this.roles,
          department_categories: this.department_categories,
        },
      })
      .afterClosed()
      .subscribe((result: WorkingContract | undefined) => {
        if (result === undefined) return;

        this.fetchUserAndContract(() => this.fetchData());
      });
  }

  openDatePickerDialogComponent(workingContractVariableCondition: WorkingContractVariableCondition): void {
    const data: DatePickerDialogData = {
      dialog_title: '¿A partir de qué fecha se iniciará el nuevo horario?',
      dialog_ok_button: 'Añadir',
      label: 'Selecciona',
      filter: null,
      selected_date: null,
      min_date: workingContractVariableCondition.end_date
        ? dayjs(workingContractVariableCondition.end_date).add(1, 'day').toDate()
        : dayjs(workingContractVariableCondition.start_date).add(1, 'day').toDate(),
    };

    this.dialog
      .open(DatePickerDialogComponent, {
        width: '400px',
        data,
      })
      .afterClosed()
      .subscribe((date: Date | undefined) => {
        if (date === undefined) return;

        const formattedDate = this.app_utils.normalizeMadridDateForApi(date, 'start');

        this.api.cloneWorkingContractVariableCondition(workingContractVariableCondition.id, formattedDate).subscribe((newRecord: any) => {
          this.fetchData();
        });
      });
  }

  deleteWorkingContractVariableCondition(workingContractVariableCondition: WorkingContractVariableCondition): void {
    if (workingContractVariableCondition.is_active) return;
    if (!confirm('¿Seguro que quieres eliminar el registro?')) return;

    this.api.deleteWorkingContractVariableCondition(workingContractVariableCondition.id).subscribe(() => {
      this.fetchData();
    });
  }

  formatDate(date: string | null): string {
    if (!date) return '-';

    return dayjs(date).format('DD/MM/YYYY');
  }

  dateIsAfterToday(date: string): boolean {
    return dayjs(date).isAfter(this.now);
  }

  getMinStartDate(record: WorkingContractVariableCondition): Date {
    const currentIndex = this.dataSource.data.findIndex(item => item.id === record.id);
    const previousCondition = this.dataSource.data[currentIndex - 1];

    if (!previousCondition || !previousCondition.end_date) return this.tomorrow;

    return dayjs(previousCondition.end_date).add(1, 'day').startOf('day').toDate();
  }

  getMaxStartDate(record: WorkingContractVariableCondition): Date | null {
    if (record.end_date) {
      return dayjs(record.end_date).startOf('day').toDate();
    }
    return null;
  }

  getMinEndDate(record: WorkingContractVariableCondition): Date {
    return dayjs(record.start_date).add(1, 'day').startOf('day').toDate();
  }

  getMaxEndDate(record: WorkingContractVariableCondition): Date | null {
    const currentIndex = this.dataSource.data.findIndex(item => item.id === record.id);
    const nextCondition = this.dataSource.data[currentIndex + 1];

    if (!nextCondition) return null;

    return dayjs(nextCondition.start_date).subtract(1, 'day').startOf('day').toDate();
  }

  saveStartDate(record: WorkingContractVariableCondition) {
    if (!this.permissions.validateDepartment(['direction', 'rrhh'])) {
      this.snack.open('No tienes permisos para modificar las fechas de inicio de contrato', 'Ok', { duration: 3000 });
      return;
    }

    const formControl = this.formGroups[record.id].get('start_date')!;
    const start_date = this.app_utils.normalizeMadridDateForApi(formControl.value, 'start');

    const end_date = record.end_date === null ? null : this.app_utils.normalizeMadridDateForApi(record.end_date, 'end');

    this.api.updateWorkingContractVariableCondition(record.id, { start_date, end_date }).subscribe((updatedRecord: any): void => {
      record = updatedRecord;
      this.fetchData();
      this.snack.open('Fecha de inicio actualizada correctamente', 'Ok', { duration: 3000 });
    });
  }

  saveEndDate(record: WorkingContractVariableCondition) {
    if (!this.permissions.validateDepartment(['direction', 'rrhh'])) {
      this.snack.open('No tienes permisos para modificar las fechas de fin de contrato', 'Ok', { duration: 3000 });
      return;
    }

    const formControl = this.getFormControl(record.id);

    const end_date = formControl.value ? this.app_utils.normalizeMadridDateForApi(formControl.value, 'end') : null;
    const start_date = this.app_utils.normalizeMadridDateForApi(record.start_date, 'start');

    this.api.updateWorkingContractVariableCondition(record.id, { start_date, end_date }).subscribe((updatedRecord: any): void => {
      record = updatedRecord;
      this.fetchData();
      this.snack.open('Fecha de fin actualizada correctamente', 'Ok', { duration: 3000 });
    });
  }

  private getFormControl(id: number) {
    return this.formGroups[id].get('end_date')!;
  }

  private isEndDateDisabled(record: WorkingContractVariableCondition): boolean {
    return this.dataSource.data.length === 1 || this.dataSource.data[this.dataSource.data.length - 1].id === record.id;
  }

  private fetchUserAndContract(after_fetch?: () => void): void {
    this.api
      .getUser(this.user.id.toString(), {
        with: 'current_working_contract..current_working_contract_variable_condition..department_categories..active_department_category_variable_condition,current_working_contract..working_contract_variable_conditions..department_categories..active_department_category_variable_condition',
      })
      .subscribe((data: User): void => {
        this.working_contract = data.current_working_contract;

        if (after_fetch) {
          after_fetch();
        }
      });
  }
}
